/* eslint-disable max-lines-per-function, complexity */
import {
  formatNumberToCommaSeparatedString,
  formatNumberToUSDCommaSeparatedString,
} from '../common/numbers/format-number-to-string'
import { MarketStatus } from '@/components/Stocks/Widgets/types'
import {
  getLastUpdatedTime,
  getLastUpdatedDate,
} from '@/revamp/pods/common/utils/get-last-update'
import { DOLLAR_SYMBOL, RUPEE_SYMBOL } from '@/root/constants'

export const getCatalogueFaqs = ({
  symbol,
  short_name,
  name,
  last_updated_v2,
  day_high,
  day_low,
  '52week_high': fiftytwo_week_high,
  '52week_low': fiftytwo_week_low,
  market_status,
  exchange_rate,
  live_price,
  '5y_stock_return': fiveYearStockReturn,
  '5y_currency_appreciation': fiveYearCurrencyAppreciation,
  curr_date,
  meta_info,
  '52w_l_t': fiftytwo_week_low_date,
  '52w_h_t': fiftytwo_week_high_date,
  market_cap_in_currency,
}: {
  symbol?: string
  short_name?: string
  name?: string
  last_updated_v2?: string
  day_high?: number
  day_low?: number
  '52week_high'?: number
  '52week_low'?: number
  market_status?: MarketStatus
  exchange_rate?: number
  price?: {
    dollar?: number
    rupee?: number
    type?: 'scu'
  }
  companyName?: string
  isMarketOpen?: boolean
  ist?: string
  live_price?: number
  dayHighPrice?: number
  dayLowPrice?: number
  fiftyTwoWeekHigh?: number
  fiftyTwoWeekLow?: number
  '5y_stock_return'?: number
  '5y_currency_appreciation'?: number
  percentBelow52WeekHigh?: number
  curr_date?: string
  meta_info?: {
    currency_symbol?: string
  }
  '52w_l_t'?: string
  '52w_h_t'?: string
  market_cap_in_currency?: number
}) => {
  const faqs: { question: string; answer: string }[] = []
  const companyName = short_name ?? name
  const isMarketOpen = market_status?.current_market_status !== 'close'
  const currency_symbol = meta_info?.currency_symbol ?? DOLLAR_SYMBOL

  if (!symbol) {
    return null
  }
  const getFractionalShares = (amount: number) => {
    if (!live_price) return null
    return (amount / live_price).toFixed(4)
  }

  const formatToTrillion = (number: number) => {
    if (!number) return ''
    const trillion = number / 1e12
    return `${trillion.toFixed(2)}T`
  }

  const getFormattedPrice = (price?: number) => {
    if (currency_symbol === DOLLAR_SYMBOL) {
      return formatNumberToUSDCommaSeparatedString(price, true)
    }
    return formatNumberToCommaSeparatedString(price, 2)
  }

  if (companyName && symbol) {
    if (typeof live_price === 'number') {
      faqs.push({
        question: `What is ${companyName} share price today?`,
        answer: `<p>${companyName} share price today is ${currency_symbol}${getFormattedPrice(
          live_price,
        )} as on <time datetime="${last_updated_v2}">${getLastUpdatedTime(
          last_updated_v2,
        )} IST</time>${isMarketOpen ? '' : ' at the close of the market'}. ${
          day_high && day_low
            ? `${companyName} share today touched a day high of ${currency_symbol}${getFormattedPrice(
                day_high,
              )} and a low of ${currency_symbol}${getFormattedPrice(day_low)}.`
            : ''
        }</p>`,
      })
    }

    if (
      typeof fiftytwo_week_high === 'number' &&
      typeof fiftytwo_week_low === 'number' &&
      typeof live_price === 'number'
    ) {
      faqs.push({
        question: `What is the 52 week high and 52 week low for ${companyName} share?`,
        answer: `<p>${companyName} share touched a 52 week high of ${currency_symbol}${getFormattedPrice(
          fiftytwo_week_high,
        )} ${
          fiftytwo_week_high_date
            ? `on <time datetime="${fiftytwo_week_high_date}">${getLastUpdatedDate(
                fiftytwo_week_high_date,
              )}</time>`
            : ''
        } and a 52 week low of ${currency_symbol}${getFormattedPrice(
          fiftytwo_week_low,
        )} ${
          fiftytwo_week_low_date
            ? `on <time datetime="${fiftytwo_week_low_date}">${getLastUpdatedDate(
                fiftytwo_week_low_date,
              )}</time>`
            : ''
        }. 
      ${companyName} stock price today i.e. <time datetime="${last_updated_v2}">${getLastUpdatedTime(
        last_updated_v2,
      )} IST</time> is ${
        isMarketOpen ? 'trending' : 'closed'
      } at ${currency_symbol}${getFormattedPrice(live_price)},which is ${(
        ((fiftytwo_week_high - live_price) / fiftytwo_week_high) *
        100
      ).toFixed(2)}% down from its 52 week high and ${(
        ((live_price - fiftytwo_week_low) / fiftytwo_week_low) *
        100
      ).toFixed(2)}% up from its 52 week low. 
</p>`,
      })
    }

    if (typeof market_cap_in_currency === 'number') {
      faqs.push({
        question: `What is ${companyName}'s market capitalisation today?`,
        answer: `<p>${companyName} market capitalisation is ${currency_symbol}${formatToTrillion(
          market_cap_in_currency,
        )} as on <time datetime="${last_updated_v2}">${getLastUpdatedTime(
          last_updated_v2,
        )} IST</time>.</p>`,
      })
    }

    faqs.push({
      question: `How to invest in ${companyName} Stock (${symbol}) from India?`,
      answer: `<div>
        <ol class="!list-none !pl-0">
          <li>
            <span class="font-medium">Step 1:</span>
            <a
              href="/investments/us-stocks/explore-all"
              rel="noreferrer noopener nofollow"
              class="text-brand-blue-azureRadiance cursor-pointer underline"
            >
              Open a free US stocks account
            </a>
            on INDmoney App and transfer your Rupees into Dollars to your
            INDmoney US stocks account. For a step by step instruction, check
            <a
              href="/blog/us-stocks/how-to-transfer-money-to-your-us-stocks-account"
              class="text-brand-blue-azureRadiance cursor-pointer underline"
            >
              How to Transfer Money to Your US Stocks Account</a> .
          </li>
          <li>
            <span class="font-medium">Step 2:</span> Search for ${companyName}
            on INDmoney app. Click on Buy button. You can invest as low as $1.5
            in ${companyName} Shares ${
              live_price && exchange_rate
                ? ` that will get you ${getFractionalShares(
                    currency_symbol === DOLLAR_SYMBOL
                      ? 1.5
                      : 1.5 * exchange_rate,
                  )} shares as per ${companyName} share price of ${currency_symbol}${getFormattedPrice(
                    live_price,
                  )} per share as on ${getLastUpdatedTime(
                    last_updated_v2,
                  )} IST.`
                : '.'
            }
          </li>
        </ol>
      </div>`,
    })

    faqs.push({
      question: `What is the minimum amount required to buy ${companyName} Stock (${symbol}) from India?`,
      answer: `<p>Indian investors can start investing in ${companyName} (${symbol}) shares with as little as <span class="font-medium">${RUPEE_SYMBOL}${exchange_rate} or $1</span> (as of <time datetime="${curr_date}">${getLastUpdatedTime(
        curr_date,
      )} IST</time>) using the INDmoney app. 
      <br/>
      For example: If you want to invest $10 ${
        exchange_rate
          ? `or ${RUPEE_SYMBOL}${(exchange_rate * 10).toFixed(2)}`
          : ''
      } in ${companyName} stock (as per the Rupee-Dollar exchange rate as on <time datetime="${curr_date}">${getLastUpdatedTime(
        curr_date,
      )} IST</time>). 
       ${
         live_price && exchange_rate
           ? ` Based on ${companyName} share’s latest price of ${currency_symbol}${getFormattedPrice(
               live_price,
             )} as on ${getLastUpdatedTime(
               last_updated_v2,
             )} IST, you will get ${getFractionalShares(
               currency_symbol === DOLLAR_SYMBOL ? 10 : 10 * exchange_rate,
             )} shares of ${companyName}.`
           : ''
       } Learn more about
          <a
            href="/blog/us-stocks/what-are-fractional-shares"
            class="text-brand-blue-azureRadiance cursor-pointer underline"
          >
            fractional shares</a> .
        </p>`,
    })

    if (fiveYearStockReturn && fiveYearCurrencyAppreciation) {
      faqs.push({
        question: `What are the returns that ${companyName} has given to Indian investors in the last 5 years?`,
        answer: `<p>${companyName} stock has given ${fiveYearStockReturn}% share price returns and ${fiveYearCurrencyAppreciation}% dollar appreciation to an Indian investor in the last 5 years.
        <br/> Read more about <a href="/blog/us-stocks/currency-exchange-rates-impact-your-us-stock-investments" class="text-brand-blue-azureRadiance cursor-pointer underline">How do Currency Exchange Rates impact your returns on US Stock Investments?</a></p>`,
      })
    }
  }

  return faqs
}
